import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, Cell } from 'recharts';
import MoringaCityMap from '../components/MoringaCityMap';
import MoringaSimulator from '../components/MoringaSimulator';
import {
  Leaf, Globe2, ShieldCheck, FileText, TrendingUp, Ship, Factory, Sprout,
  MapPin, Coins, ArrowRight, CheckCircle2, Lock, Layers, BarChart3, Boxes,
  Banknote, Landmark, ChevronDown, Wheat, Languages, ChevronLeft, ChevronRight,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO = '/moringa-logo.png';

const IMG = {
  hero: 'https://images.unsplash.com/photo-1728075995396-b30ca422bc9d?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000',
  plantation: 'https://images.unsplash.com/photo-1622663859218-1aab0d85ad2e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600',
  processing: 'https://images.unsplash.com/photo-1651525670054-279c154bc3b6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600',
  harvest: 'https://images.unsplash.com/photo-1635174815475-1c624f86808b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600',
  export: 'https://images.unsplash.com/photo-1605745341112-85968b19335b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600',
};

const SLIDE_IMAGES = [
  'https://images.unsplash.com/photo-1607207219455-37c3a0a0f3a7?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000',
  'https://images.unsplash.com/photo-1545292470-391a7b77b8a2?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000',
  'https://images.unsplash.com/photo-1622383563227-04401ab4e5ea?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000',
  'https://images.unsplash.com/photo-1651525670054-279c154bc3b6?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000',
  'https://images.unsplash.com/photo-1605745341112-85968b19335b?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000',
];

// ---- Bilingual copy (EN / PT) ----
const T = {
  en: {
    nav_invest: 'Start Investing', nav_account: 'Open Free Account', nav_ecosystem: 'Explore Ecosystem',
    hero_tag: 'CAPITAL CONNECTED TO REAL PRODUCTION',
    hero_h1: 'Invest in a national Moringa production ecosystem',
    hero_sub: 'Access real agricultural production through the Youse Capital Single Account.',
    cta_start: 'Start Investing', cta_open: 'Open Free Single Account', cta_explore: 'Explore the Ecosystem',
    cta_view: 'View My Investment',
    n_min: 'Minimum Investment', n_ret: 'Target Monthly Return', n_setup: 'Single Account Setup',
    n_net: 'Agricultural Network', n_geo: 'Geographic Reach', n_asset: 'Asset Class',
    free: 'FREE', brazil: 'Brazil', agri: 'Agriculture / Commodity', awaiting: 'Awaiting data',
    ha_title: 'A National Agricultural Network', ha_sub: 'Across Brazil',
    map_title: 'Brazil Agricultural Network', map_sub: 'Operations distributed across regions — some locations are protected for strategic reasons.',
    ne_focus: 'Northeast Focus', in_northeast: 'concentrated in the Northeast',
    growth_title: 'Growth & Performance', growth_sub: 'Expansion of the agricultural network, production and revenue over time.',
    g_network: 'Agricultural Network (ha)', g_production: 'Production & Exports (tons)', g_revenue: 'Revenue (US$)',
    g_prod_legend: 'Production', g_exp_legend: 'Exports', verified_badge: 'Verified',
    regions: { NORTH: 'North', NORTHEAST: 'Northeast', 'CENTRAL-WEST': 'Central-West', SOUTHEAST: 'Southeast', SOUTH: 'South' },
    sec_title: 'Investment Security Structure',
    sec_sub: 'Security demonstrated through structure, data and documentation — not slogans.',
    layers: [
      ['Youse Capital Single Account', 'Financial access and investor account.'],
      ['Private Funding', 'Funding structure associated with Moringa Bank.'],
      ['Real Agricultural Operations', 'Underlying agricultural activity across Brazil.'],
      ['Documentation', 'Land, production, commercial and legal documentation.'],
      ['Traceability', 'Production and transaction records.'],
      ['Commercialization', 'Domestic and international sales.'],
      ['Investor Reporting', 'Continuous visibility through the Single Account.'],
    ],
    pf_title: 'Moringa Bank — Private Funding Structure',
    pf_total: 'Total Private Funding', pf_alloc: 'Allocated', pf_avail: 'Available',
    pf_status: 'Funding Status', pf_verif: 'Last Verification',
    flow_title: 'How Your Capital Works',
    flow: ['You Invest', 'Capital allocated to the Moringa ecosystem', 'Agricultural production', 'Processing', 'Commercialization', 'Export', 'Revenue', 'Investor distribution'],
    cb_title: 'Moringa Commodity Book',
    cb_prod: 'Production Volume', cb_inv: 'Inventory', cb_proc: 'Processing Capacity', cb_rev: 'Total Revenue',
    prod_title: 'Live Production', exp_title: 'Global Moringa Trade',
    exp_vol: 'Export Volume', exp_rev: 'Export Revenue', exp_countries: 'Countries Served', exp_contracts: 'Commercial Contracts',
    dr_title: 'Investment Data Room',
    dr_sub: 'Documentation becomes available here as the operation publishes verified records.',
    dr_cats: ['Corporate', 'Private Funding', 'Agricultural Areas', 'Production', 'Commercial Agreements', 'Export', 'Processing', 'Insurance', 'Environmental', 'Financial', 'Legal', 'Reports'],
    dr_pending: 'Pending verification',
    sim_title: 'Investment Simulator', sim_amount: 'Investment Amount',
    sim_month: 'Projected Monthly Range', sim_year: 'Projected Annual Range', sim_mb: 'MB Tokens Received',
    sim_reinvest: 'Reinvest distributions', sim_proj: 'Projected portfolio value (12 months)',
    sim_note: 'Projected / target figures. Not a guarantee of return.',
    fsa_title: 'Your Single Account. No Setup Fee.',
    fsa_sub: 'Open your Youse Capital Single Account and access the Moringa investment ecosystem.',
    fsa_setup: 'Setup', fsa_min: 'Minimum Moringa Investment',
    final_a: 'From Capital', final_b: 'to Production', final_c: 'to Global Commerce.',
    final_sub: 'Access a national agricultural ecosystem through your Youse Capital Single Account.',
    inst_1: 'One Account.', inst_2: 'One Ecosystem.', inst_3: 'Real Agricultural Exposure.',
    verified: 'Verified data', updated: 'Last updated',
    slides: [
      ['The Power of Moringa', 'A resilient, high-value crop driving a national production ecosystem.'],
      ['National Agricultural Network', '+50,000 hectares cultivated across Brazil.'],
      ['Rooted in Real Production', 'From soil to harvest — capital connected to tangible assets.'],
      ['Processing & Commercialization', 'Moringa transformed into high-demand commodities.'],
      ['Global Export Ecosystem', 'Reaching international markets through structured trade.'],
    ],
    pillars_title: 'Why Moringa Agro Invest',
    pillars: [
      ['National Scale', '+50,000 hectares across Brazil.'],
      ['Single Account', 'One financial ecosystem.'],
      ['Free Setup', 'No Single Account setup fee.'],
      ['Private Funding', 'Structured funding layer.'],
      ['Real Production', 'Real agricultural operations.'],
      ['Traceability', 'Operational visibility.'],
      ['Commodity', 'Production and commercialization data.'],
      ['Global Trade', 'International export exposure.'],
    ],
  },
  pt: {
    nav_invest: 'Começar a Investir', nav_account: 'Abrir Conta Grátis', nav_ecosystem: 'Explorar Ecossistema',
    hero_tag: 'CAPITAL CONECTADO À PRODUÇÃO REAL',
    hero_h1: 'Invista em um ecossistema nacional de produção de Moringa',
    hero_sub: 'Acesse produção agrícola real através da Single Account da Youse Capital.',
    cta_start: 'Começar a Investir', cta_open: 'Abrir Single Account Grátis', cta_explore: 'Explorar o Ecossistema',
    cta_view: 'Ver Meu Investimento',
    n_min: 'Investimento Mínimo', n_ret: 'Retorno Mensal Alvo', n_setup: 'Setup da Single Account',
    n_net: 'Rede Agrícola', n_geo: 'Alcance Geográfico', n_asset: 'Classe de Ativo',
    free: 'GRÁTIS', brazil: 'Brasil', agri: 'Agricultura / Commodity', awaiting: 'Aguardando dados',
    ha_title: 'Uma Rede Agrícola Nacional', ha_sub: 'Distribuída pelo Brasil',
    map_title: 'Rede Agrícola no Brasil', map_sub: 'Operações distribuídas por regiões — algumas localizações são protegidas por razões estratégicas.',
    ne_focus: 'Foco no Nordeste', in_northeast: 'concentrados no Nordeste',
    growth_title: 'Crescimento & Performance', growth_sub: 'Expansão da rede agrícola, produção e receita ao longo do tempo.',
    g_network: 'Rede Agrícola (ha)', g_production: 'Produção & Exportações (ton)', g_revenue: 'Receita (US$)',
    g_prod_legend: 'Produção', g_exp_legend: 'Exportações', verified_badge: 'Verificado',
    regions: { NORTH: 'Norte', NORTHEAST: 'Nordeste', 'CENTRAL-WEST': 'Centro-Oeste', SOUTHEAST: 'Sudeste', SOUTH: 'Sul' },
    sec_title: 'Estrutura de Segurança do Investimento',
    sec_sub: 'Segurança demonstrada por estrutura, dados e documentação — não por slogans.',
    layers: [
      ['Single Account Youse Capital', 'Acesso financeiro e conta do investidor.'],
      ['Private Funding', 'Estrutura de funding associada ao Moringa Bank.'],
      ['Operações Agrícolas Reais', 'Atividade agrícola subjacente pelo Brasil.'],
      ['Documentação', 'Documentação fundiária, produtiva, comercial e legal.'],
      ['Rastreabilidade', 'Registros de produção e transações.'],
      ['Comercialização', 'Vendas nacionais e internacionais.'],
      ['Relatórios ao Investidor', 'Visibilidade contínua pela Single Account.'],
    ],
    pf_title: 'Moringa Bank — Estrutura de Private Funding',
    pf_total: 'Private Funding Total', pf_alloc: 'Alocado', pf_avail: 'Disponível',
    pf_status: 'Status do Funding', pf_verif: 'Última Verificação',
    flow_title: 'Como Seu Capital Funciona',
    flow: ['Você Investe', 'Capital alocado ao ecossistema Moringa', 'Produção agrícola', 'Processamento', 'Comercialização', 'Exportação', 'Receita', 'Distribuição ao investidor'],
    cb_title: 'Moringa Commodity Book',
    cb_prod: 'Volume de Produção', cb_inv: 'Estoque', cb_proc: 'Capacidade de Processamento', cb_rev: 'Receita Total',
    prod_title: 'Produção ao Vivo', exp_title: 'Comércio Global de Moringa',
    exp_vol: 'Volume de Exportação', exp_rev: 'Receita de Exportação', exp_countries: 'Países Atendidos', exp_contracts: 'Contratos Comerciais',
    dr_title: 'Data Room do Investimento',
    dr_sub: 'A documentação fica disponível aqui à medida que a operação publica registros verificados.',
    dr_cats: ['Corporativo', 'Private Funding', 'Áreas Agrícolas', 'Produção', 'Contratos Comerciais', 'Exportação', 'Processamento', 'Seguros', 'Ambiental', 'Financeiro', 'Jurídico', 'Relatórios'],
    dr_pending: 'Pendente de verificação',
    sim_title: 'Simulador de Investimento', sim_amount: 'Valor do Investimento',
    sim_month: 'Faixa Mensal Projetada', sim_year: 'Faixa Anual Projetada', sim_mb: 'Tokens MB Recebidos',
    sim_reinvest: 'Reinvestir distribuições', sim_proj: 'Valor projetado da carteira (12 meses)',
    sim_note: 'Valores projetados / alvo. Não é garantia de retorno.',
    fsa_title: 'Sua Single Account. Sem Taxa de Setup.',
    fsa_sub: 'Abra sua Single Account na Youse Capital e acesse o ecossistema de investimento Moringa.',
    fsa_setup: 'Setup', fsa_min: 'Investimento Mínimo em Moringa',
    final_a: 'Do Capital', final_b: 'à Produção', final_c: 'ao Comércio Global.',
    final_sub: 'Acesse um ecossistema agrícola nacional através da sua Single Account Youse Capital.',
    inst_1: 'Uma Conta.', inst_2: 'Um Ecossistema.', inst_3: 'Exposição Agrícola Real.',
    verified: 'Dados verificados', updated: 'Atualizado em',
    slides: [
      ['O Poder da Moringa', 'Uma cultura resiliente e de alto valor que impulsiona um ecossistema produtivo nacional.'],
      ['Rede Agrícola Nacional', '+50.000 hectares cultivados pelo Brasil.'],
      ['Enraizado em Produção Real', 'Do solo à colheita — capital conectado a ativos tangíveis.'],
      ['Processamento & Comercialização', 'Moringa transformada em commodities de alta demanda.'],
      ['Ecossistema Global de Exportação', 'Alcançando mercados internacionais por meio de comércio estruturado.'],
    ],
    pillars_title: 'Por que Moringa Agro Invest',
    pillars: [
      ['Escala Nacional', '+50.000 hectares pelo Brasil.'],
      ['Single Account', 'Um ecossistema financeiro.'],
      ['Setup Grátis', 'Sem taxa de setup da Single Account.'],
      ['Private Funding', 'Camada estruturada de funding.'],
      ['Produção Real', 'Operações agrícolas reais.'],
      ['Rastreabilidade', 'Visibilidade operacional.'],
      ['Commodity', 'Dados de produção e comercialização.'],
      ['Comércio Global', 'Exposição à exportação internacional.'],
    ],
  },
};

const fmtNum = (v) => (v === null || v === undefined || v === '' ? null : Number(v).toLocaleString('en-US'));
const fmtUSD = (v) => (v === null || v === undefined || v === '' ? null : '$' + Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 }));

const fade = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

const Stat = ({ label, value, pending, gold }) => (
  <div>
    <p className="text-[11px] uppercase tracking-[0.18em] text-emerald-200/60">{label}</p>
    {value ? (
      <p className={`mt-1 text-2xl md:text-3xl font-bold ${gold ? 'text-[#e9c767]' : 'text-white'}`}>{value}</p>
    ) : (
      <p className="mt-1 text-sm font-medium text-emerald-200/40 italic">{pending}</p>
    )}
  </div>
);

const Section = ({ children, className = '' }) => (
  <motion.section
    variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }}
    className={`relative mx-auto w-full max-w-7xl px-6 py-20 md:py-28 ${className}`}
  >
    {children}
  </motion.section>
);

const MoringaSlider = ({ captions }) => {
  const [idx, setIdx] = useState(0);
  const n = SLIDE_IMAGES.length;
  const go = useCallback((d) => setIdx((p) => (p + d + n) % n), [n]);
  useEffect(() => {
    const id = setInterval(() => setIdx((p) => (p + 1) % n), 5000);
    return () => clearInterval(id);
  }, [n]);
  return (
    <section className="relative h-[70vh] min-h-[460px] w-full overflow-hidden" data-testid="moringa-slider">
      <AnimatePresence mode="sync">
        <motion.div
          key={idx}
          initial={{ opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.1, ease: 'easeInOut' }}
          className="absolute inset-0"
        >
          <img src={SLIDE_IMAGES[idx]} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#07160f] via-[#07160f]/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#07160f] via-transparent to-transparent" />
        </motion.div>
      </AnimatePresence>
      <div className="relative z-10 mx-auto max-w-7xl px-6 h-full flex flex-col justify-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <div className="flex items-center gap-2 mb-4">
              <span className="h-px w-10 bg-[#e9c767]" />
              <span className="text-[11px] font-semibold tracking-[0.25em] text-[#e9c767]">
                {String(idx + 1).padStart(2, '0')} / {String(n).padStart(2, '0')}
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold leading-[1.05]">{captions[idx]?.[0]}</h2>
            <p className="mt-4 text-lg text-emerald-100/75 max-w-lg">{captions[idx]?.[1]}</p>
          </motion.div>
        </AnimatePresence>
      </div>
      <button onClick={() => go(-1)} aria-label="Previous"
        className="absolute left-5 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full border border-[#e9c767]/40 bg-[#07160f]/50 backdrop-blur flex items-center justify-center text-[#e9c767] hover:bg-[#e9c767]/15 transition-colors" data-testid="slider-prev">
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button onClick={() => go(1)} aria-label="Next"
        className="absolute right-5 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full border border-[#e9c767]/40 bg-[#07160f]/50 backdrop-blur flex items-center justify-center text-[#e9c767] hover:bg-[#e9c767]/15 transition-colors" data-testid="slider-next">
        <ChevronRight className="w-5 h-5" />
      </button>
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {SLIDE_IMAGES.map((_, i) => (
          <button key={i} onClick={() => setIdx(i)} aria-label={`Slide ${i + 1}`}
            className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-8 bg-[#e9c767]' : 'w-3 bg-white/30 hover:bg-white/50'}`}
            data-testid={`slider-dot-${i}`} />
        ))}
      </div>
    </section>
  );
};

export default function MoringaInvestPage() {
  const { user } = useAuth();
  const { language, setLanguage } = useLanguage();
  const lang = language === 'pt' ? 'pt' : 'en';
  const t = T[lang];
  const [cfg, setCfg] = useState(null);
  const [amount, setAmount] = useState(500);
  const [reinvest, setReinvest] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/moringa/public`).then((r) => setCfg(r.data)).catch(() => setCfg({}));
  }, []);

  const c = cfg || {};
  const minInv = c.min_investment || 500;
  const rMin = c.target_return_min ?? 2;
  const rMax = c.target_return_max ?? 3;
  const mbPerUsd = c.mb_per_usd ?? 1;
  const mbSym = c.mb_symbol || 'MBT';
  const pend = t.awaiting;

  const simData = useMemo(() => {
    const data = [];
    let valLow = amount, valHigh = amount;
    for (let m = 0; m <= 12; m++) {
      data.push({ m: `M${m}`, low: Math.round(valLow), high: Math.round(valHigh) });
      if (reinvest) { valLow *= 1 + rMin / 100; valHigh *= 1 + rMax / 100; }
      else { valLow += (amount * rMin) / 100; valHigh += (amount * rMax) / 100; }
    }
    return data;
  }, [amount, reinvest, rMin, rMax]);

  const scrollTo = useCallback((id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const startHref = user ? '/dashboard' : '/signup';

  return (
    <div className="min-h-screen bg-[#07160f] text-white font-[Outfit,sans-serif] selection:bg-[#e9c767]/30" data-testid="moringa-landing">
      {/* NAV */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#07160f]/70 border-b border-[#e9c767]/15">
        <div className="mx-auto max-w-7xl px-6 h-16 md:h-20 flex items-center justify-between">
          <Link to="/moringa-invest" className="flex items-center gap-3" data-testid="moringa-logo-link">
            <img src={LOGO} alt="Moringa Agro Invest" className="h-14 md:h-16 w-auto drop-shadow-[0_0_12px_rgba(233,199,103,0.25)]" />
            <span className="hidden sm:block text-sm font-semibold tracking-wide text-[#e9c767]">MORINGA AGRO INVEST</span>
          </Link>
          <div className="flex items-center gap-2 md:gap-3">
            <button
              onClick={() => setLanguage(lang === 'pt' ? 'en' : 'pt')}
              className="flex items-center gap-1.5 rounded-full border border-[#e9c767]/30 px-3 py-1.5 text-xs font-medium text-[#e9c767] hover:bg-[#e9c767]/10 transition-colors"
              data-testid="moringa-lang-toggle"
            >
              <Languages className="w-3.5 h-3.5" /> {lang === 'pt' ? 'EN' : 'PT'}
            </button>
            <Link
              to={user ? '/moringa-dashboard' : startHref}
              className="rounded-full bg-[#e9c767] px-4 md:px-5 py-2 text-xs md:text-sm font-semibold text-[#07160f] hover:brightness-110 transition-all shadow-[0_0_24px_rgba(233,199,103,0.25)]"
              data-testid="moringa-nav-cta"
            >
              {user ? t.cta_view : t.nav_account}
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG.hero} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#07160f]/80 via-[#07160f]/85 to-[#07160f]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(233,199,103,0.12),transparent_55%)]" />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 w-full">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#e9c767]/30 bg-[#e9c767]/5 px-4 py-1.5 mb-7">
              <Leaf className="w-3.5 h-3.5 text-[#e9c767]" />
              <span className="text-[11px] md:text-xs font-semibold tracking-[0.2em] text-[#e9c767]">{t.hero_tag}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
              {t.hero_h1}
            </h1>
            <p className="mt-6 text-base md:text-lg text-emerald-100/70 max-w-xl">{t.hero_sub}</p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to={startHref} className="group inline-flex items-center gap-2 rounded-full bg-[#e9c767] px-7 py-3.5 text-sm font-bold text-[#07160f] hover:brightness-110 transition-all shadow-[0_0_30px_rgba(233,199,103,0.3)]" data-testid="hero-start-btn">
                {t.cta_start} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/signup" className="inline-flex items-center gap-2 rounded-full border border-[#e9c767]/40 px-7 py-3.5 text-sm font-semibold text-[#e9c767] hover:bg-[#e9c767]/10 transition-colors" data-testid="hero-open-btn">
                {t.cta_open}
              </Link>
              <button onClick={() => scrollTo('ecosystem')} className="inline-flex items-center gap-2 rounded-full px-5 py-3.5 text-sm font-medium text-emerald-100/70 hover:text-white transition-colors">
                {t.cta_explore} <ChevronDown className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* NUMBERS BAR */}
      <div className="relative border-y border-[#e9c767]/15 bg-[#0a1f15]">
        <div className="mx-auto max-w-7xl px-6 py-9 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
          <Stat label={t.n_min} value={fmtUSD(minInv)} pending={pend} gold />
          <Stat label={t.n_ret} value={`${rMin}% – ${rMax}%`} pending={pend} />
          <Stat label={t.n_setup} value={t.free} pending={pend} gold />
          <Stat label={t.n_net} value={c.total_hectares ? `+${fmtNum(c.total_hectares)} ha` : null} pending={pend} />
          <Stat label={t.n_geo} value={t.brazil} pending={pend} />
          <Stat label={t.n_asset} value={t.agri} pending={pend} />
        </div>
      </div>

      <div id="ecosystem" />

      {/* IMPACT SLIDER */}
      <MoringaSlider captions={t.slides} />

      {/* +50,000 HECTARES */}
      <Section>
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-semibold tracking-[0.2em] text-[#e9c767] mb-4">{t.ha_sub.toUpperCase()}</p>
            <h2 className="text-3xl md:text-4xl font-bold leading-tight">{t.ha_title}</h2>
            <div className="mt-8 flex items-end gap-4">
              <span className="text-6xl md:text-7xl font-extrabold text-[#e9c767] drop-shadow-[0_0_30px_rgba(233,199,103,0.25)]">
                +{fmtNum(c.total_hectares || 50000)}
              </span>
              <span className="pb-3 text-lg text-emerald-100/70">hectares</span>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-5">
              <Stat label={lang === 'pt' ? 'Áreas Ativas' : 'Active Areas'} value={c.active_hectares ? `${fmtNum(c.active_hectares)} ha` : null} pending={pend} />
              <Stat label={lang === 'pt' ? 'Áreas Disponíveis' : 'Available Areas'} value={c.available_hectares ? `${fmtNum(c.available_hectares)} ha` : null} pending={pend} />
              <Stat label={lang === 'pt' ? 'Estados' : 'States'} value={fmtNum(c.states_count)} pending={pend} />
              <Stat label={lang === 'pt' ? 'Projetos' : 'Projects'} value={fmtNum(c.projects_count)} pending={pend} />
            </div>
          </div>
          <div className="relative rounded-3xl overflow-hidden border border-[#e9c767]/15 shadow-2xl">
            <img src={IMG.plantation} alt="Moringa plantation" className="w-full h-[420px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#07160f] via-transparent to-transparent" />
          </div>
        </div>
      </Section>

      {/* BRAZIL MAP (dynamic, Northeast focus) */}
      <div className="bg-[#0a1f15] border-y border-[#e9c767]/10">
        <Section>
          <div className="flex items-center gap-3 mb-3"><MapPin className="w-5 h-5 text-[#e9c767]" /><h2 className="text-3xl md:text-4xl font-bold">{t.map_title}</h2></div>
          <p className="text-emerald-100/60 max-w-2xl mb-10">{t.map_sub}</p>
          <MoringaCityMap cities={c.cities || []} labels={{ hint: lang === 'pt' ? 'Passe o mouse sobre uma torre para ver os dados do projeto' : 'Hover a city tower to see project details', share: lang === 'pt' ? 'Alocação' : 'Allocation', hectares: 'Hectares', planting: lang === 'pt' ? 'Plantio' : 'Planting', planted: lang === 'pt' ? 'Plantado' : 'Planted', harvest: lang === 'pt' ? 'Próxima colheita' : 'Next harvest', yield: lang === 'pt' ? 'Produção estimada' : 'Expected yield' }} />
        </Section>
      </div>

      {/* GROWTH & PERFORMANCE CHARTS */}
      <Section>
        <div className="flex items-center gap-3 mb-3"><TrendingUp className="w-5 h-5 text-[#e9c767]" /><h2 className="text-3xl md:text-4xl font-bold">{t.growth_title}</h2></div>
        <p className="text-emerald-100/60 max-w-2xl mb-10">{t.growth_sub}</p>
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="rounded-3xl border border-[#e9c767]/15 bg-[#0a1f15] p-6">
            <p className="text-xs uppercase tracking-[0.18em] text-emerald-200/60 mb-4">{t.g_network}</p>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={c.growth || []}>
                <defs><linearGradient id="gNet" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3ba776" stopOpacity={0.6} /><stop offset="100%" stopColor="#3ba776" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(233,199,103,0.08)" />
                <XAxis dataKey="period" stroke="rgba(255,255,255,0.4)" fontSize={10} interval={3} />
                <YAxis stroke="rgba(255,255,255,0.4)" fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ background: '#07160f', border: '1px solid rgba(233,199,103,0.3)', borderRadius: 12, color: '#fff' }} formatter={(v) => `${Number(v).toLocaleString('en-US')} ha`} />
                <Area type="monotone" dataKey="hectares" stroke="#3ba776" strokeWidth={2.5} fill="url(#gNet)" animationDuration={1400} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="rounded-3xl border border-[#e9c767]/15 bg-[#0a1f15] p-6">
            <p className="text-xs uppercase tracking-[0.18em] text-emerald-200/60 mb-4">{t.g_production}</p>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={c.growth || []}>
                <defs>
                  <linearGradient id="gProd" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#e9c767" stopOpacity={0.55} /><stop offset="100%" stopColor="#e9c767" stopOpacity={0} /></linearGradient>
                  <linearGradient id="gExp" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#5ec2e8" stopOpacity={0.45} /><stop offset="100%" stopColor="#5ec2e8" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(233,199,103,0.08)" />
                <XAxis dataKey="period" stroke="rgba(255,255,255,0.4)" fontSize={10} interval={3} />
                <YAxis stroke="rgba(255,255,255,0.4)" fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ background: '#07160f', border: '1px solid rgba(233,199,103,0.3)', borderRadius: 12, color: '#fff' }} formatter={(v) => `${Number(v).toLocaleString('en-US')} t`} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area type="monotone" name={t.g_prod_legend} dataKey="production" stroke="#e9c767" strokeWidth={2.5} fill="url(#gProd)" animationDuration={1400} />
                <Area type="monotone" name={t.g_exp_legend} dataKey="exports" stroke="#5ec2e8" strokeWidth={2} fill="url(#gExp)" animationDuration={1600} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="rounded-3xl border border-[#e9c767]/15 bg-[#0a1f15] p-6">
            <p className="text-xs uppercase tracking-[0.18em] text-emerald-200/60 mb-4">{t.g_revenue}</p>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={c.growth || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(233,199,103,0.08)" />
                <XAxis dataKey="period" stroke="rgba(255,255,255,0.4)" fontSize={10} interval={3} />
                <YAxis stroke="rgba(255,255,255,0.4)" fontSize={11} tickFormatter={(v) => `$${(v / 1000000).toFixed(0)}M`} />
                <Tooltip contentStyle={{ background: '#07160f', border: '1px solid rgba(233,199,103,0.3)', borderRadius: 12, color: '#fff' }} formatter={(v) => `$${Number(v).toLocaleString('en-US')}`} />
                <Bar dataKey="revenue" radius={[6, 6, 0, 0]} animationDuration={1400}>
                  {(c.growth || []).map((_, i) => (<Cell key={i} fill="#e9c767" fillOpacity={0.55 + i * 0.09} />))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Section>

      {/* SECURITY STRUCTURE */}
      <Section>
        <div className="flex items-center gap-3 mb-3"><ShieldCheck className="w-5 h-5 text-[#e9c767]" /><h2 className="text-3xl md:text-4xl font-bold">{t.sec_title}</h2></div>
        <p className="text-emerald-100/60 max-w-2xl mb-10">{t.sec_sub}</p>
        <div className="space-y-3">
          {t.layers.map((l, i) => (
            <motion.div key={i} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="flex items-center gap-5 rounded-2xl border border-[#e9c767]/12 bg-[#0a1f15] p-5 hover:border-[#e9c767]/35 transition-colors">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#e9c767]/10 border border-[#e9c767]/25 flex items-center justify-center text-[#e9c767] font-bold">
                {String(i + 1).padStart(2, '0')}
              </div>
              <div><p className="font-semibold text-white">{l[0]}</p><p className="text-sm text-emerald-100/60">{l[1]}</p></div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* PRIVATE FUNDING */}
      <div className="bg-[#0a1f15] border-y border-[#e9c767]/10">
        <Section>
          <div className="flex items-center gap-3 mb-10"><Landmark className="w-5 h-5 text-[#e9c767]" /><h2 className="text-3xl md:text-4xl font-bold">{t.pf_title}</h2></div>
          <div className="grid md:grid-cols-3 gap-5">
            {[[t.pf_total, fmtUSD(c.funding_total)], [t.pf_alloc, fmtUSD(c.funding_allocated)], [t.pf_avail, fmtUSD(c.funding_available)], [t.pf_status, c.funding_status], [t.pf_verif, c.funding_last_verification]].map(([lbl, val], i) => (
              <div key={i} className="rounded-2xl border border-[#e9c767]/15 bg-[#07160f]/60 p-6"><Stat label={lbl} value={val} pending={pend} gold={i < 3} /></div>
            ))}
          </div>
        </Section>
      </div>

      {/* HOW CAPITAL WORKS */}
      <Section>
        <div className="flex items-center gap-3 mb-10"><Coins className="w-5 h-5 text-[#e9c767]" /><h2 className="text-3xl md:text-4xl font-bold">{t.flow_title}</h2></div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {t.flow.map((step, i) => (
            <motion.div key={i} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="relative rounded-2xl border border-[#e9c767]/12 bg-[#0a1f15] p-5">
              <span className="text-[#e9c767]/40 text-sm font-bold">{String(i + 1).padStart(2, '0')}</span>
              <p className="mt-2 font-medium text-white text-sm leading-snug">{step}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* MBT TOKEN — dollar-backed (English only) */}
      <div className="relative overflow-hidden border-y border-[#e9c767]/15 bg-[#0a1f15]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_30%,rgba(233,199,103,0.1),transparent_55%)]" />
        <Section className="relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-[#e9c767]/30 bg-[#e9c767]/5 px-4 py-1.5 mb-5">
                <Coins className="w-3.5 h-3.5 text-[#e9c767]" />
                <span className="text-[11px] font-semibold tracking-[0.2em] text-[#e9c767]">MBT · MORINGA BANK TOKEN</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold leading-tight">A dollar-backed token for every investment</h2>
              <p className="mt-5 text-emerald-100/70 max-w-xl">
                Each allocation into Moringa Agro Invest issues <span className="text-[#e9c767] font-semibold">MBT (Moringa Bank Token)</span> directly to your Youse Capital Single Account. MBT is <span className="text-white font-semibold">backed by the US Dollar</span> at a <span className="text-white font-semibold">1 MBT = US$ 1.00</span> reference, giving your position a transparent, dollar-denominated unit of account.
              </p>
              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="rounded-2xl border border-[#e9c767]/15 bg-[#07160f]/60 p-5">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-emerald-200/60">Peg</p>
                  <p className="mt-1 text-2xl font-bold text-[#e9c767]">1 MBT = US$ 1.00</p>
                </div>
                <div className="rounded-2xl border border-[#e9c767]/15 bg-[#07160f]/60 p-5">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-emerald-200/60">Backing</p>
                  <p className="mt-1 text-2xl font-bold text-white">US Dollar</p>
                </div>
              </div>
              <ul className="mt-6 space-y-3">
                {[
                  'Issued instantly on each investment into the Moringa ecosystem.',
                  'Dollar-pegged reference value — 1 MBT equals US$ 1.00.',
                  'Held and tracked inside your Youse Capital Single Account.',
                  'Represents your allocation to real agricultural production.',
                ].map((li, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-emerald-100/75">
                    <CheckCircle2 className="w-4 h-4 text-[#e9c767] mt-0.5 flex-shrink-0" /> {li}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex justify-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.85 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
                className="relative w-72 h-72 rounded-full bg-gradient-to-br from-[#e9c767] to-[#b8902f] flex items-center justify-center shadow-[0_0_80px_rgba(233,199,103,0.3)]"
              >
                <div className="absolute inset-3 rounded-full border-2 border-[#07160f]/30" />
                <div className="text-center text-[#07160f]">
                  <Coins className="w-12 h-12 mx-auto mb-2" />
                  <p className="text-5xl font-extrabold tracking-tight">MBT</p>
                  <p className="text-sm font-semibold mt-1">1 MBT = US$ 1.00</p>
                  <p className="text-[11px] font-medium mt-1 opacity-70">Dollar-backed</p>
                </div>
              </motion.div>
            </div>
          </div>
        </Section>
      </div>

      {/* COMMODITY BOOK + PRODUCTION + EXPORT */}
      <div className="bg-[#0a1f15] border-y border-[#e9c767]/10">
        <Section>
          <div className="flex items-center gap-3 mb-10"><Boxes className="w-5 h-5 text-[#e9c767]" /><h2 className="text-3xl md:text-4xl font-bold">{t.cb_title}</h2></div>
          <div className="grid md:grid-cols-4 gap-5 mb-16">
            {[[t.cb_prod, fmtNum(c.production_volume)], [t.cb_inv, fmtNum(c.inventory_volume)], [t.cb_proc, fmtNum(c.processing_capacity)], [t.cb_rev, fmtUSD(c.total_revenue)]].map(([lbl, val], i) => (
              <div key={i} className="rounded-2xl border border-[#e9c767]/15 bg-[#07160f]/60 p-6"><Stat label={lbl} value={val} pending={pend} gold={i === 3} /></div>
            ))}
          </div>
          {(c.products || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-16">
              {(c.products || []).map((p, i) => (
                <span key={i} className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm ${p.status === 'ACTIVE' ? 'border-[#e9c767]/30 bg-[#e9c767]/10 text-[#e9c767]' : 'border-emerald-400/20 bg-emerald-400/5 text-emerald-200/70'}`}>
                  <Leaf className="w-3.5 h-3.5" /> {p.name}
                </span>
              ))}
            </div>
          )}
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="relative rounded-3xl overflow-hidden border border-[#e9c767]/15">
              <img src={IMG.processing} alt="Processing" className="w-full h-72 object-cover opacity-80" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#07160f] to-transparent" />
              <div className="absolute bottom-5 left-6 flex items-center gap-2"><Factory className="w-5 h-5 text-[#e9c767]" /><span className="font-semibold">{t.prod_title}</span></div>
            </div>
            <div className="relative rounded-3xl overflow-hidden border border-[#e9c767]/15">
              <img src={IMG.export} alt="Export" className="w-full h-72 object-cover opacity-80" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#07160f] to-transparent" />
              <div className="absolute bottom-5 left-6 flex items-center gap-2"><Ship className="w-5 h-5 text-[#e9c767]" /><span className="font-semibold">{t.exp_title}</span></div>
            </div>
          </div>
          <div className="mt-6 grid md:grid-cols-4 gap-5">
            {[[t.exp_vol, fmtNum(c.export_volume)], [t.exp_rev, fmtUSD(c.export_revenue)], [t.exp_countries, fmtNum(c.countries_served)], [t.exp_contracts, fmtNum(c.commercial_contracts)]].map(([lbl, val], i) => (
              <div key={i} className="rounded-2xl border border-[#e9c767]/15 bg-[#07160f]/60 p-6"><Stat label={lbl} value={val} pending={pend} /></div>
            ))}
          </div>
        </Section>
      </div>

      {/* DATA ROOM */}
      <Section>
        <div className="flex items-center gap-3 mb-3"><FileText className="w-5 h-5 text-[#e9c767]" /><h2 className="text-3xl md:text-4xl font-bold">{t.dr_title}</h2></div>
        <p className="text-emerald-100/60 max-w-2xl mb-10">{t.dr_sub}</p>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {t.dr_cats.map((cat, i) => (
            <div key={i} className="flex items-center justify-between rounded-xl border border-[#e9c767]/12 bg-[#0a1f15] px-5 py-4">
              <div className="flex items-center gap-3">
                {c.data_room_verified ? <FileText className="w-4 h-4 text-[#e9c767]" /> : <Lock className="w-4 h-4 text-[#e9c767]/60" />}
                <span className="text-sm font-medium">{cat}</span>
              </div>
              {c.data_room_verified ? (
                <span className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-emerald-400"><CheckCircle2 className="w-3.5 h-3.5" /> {t.verified_badge}</span>
              ) : (
                <span className="text-[10px] uppercase tracking-wide text-emerald-200/40">{t.dr_pending}</span>
              )}
            </div>
          ))}
        </div>
      </Section>

      {/* SIMULATOR */}
      <div className="bg-[#0a1f15] border-y border-[#e9c767]/10">
        <Section>
          <div className="flex items-center gap-3 mb-10"><BarChart3 className="w-5 h-5 text-[#e9c767]" /><h2 className="text-3xl md:text-4xl font-bold">{t.sim_title}</h2></div>
          <div className="grid lg:grid-cols-5 gap-8 items-start">
            <div className="lg:col-span-2 space-y-6">
              <div>
                <div className="flex items-end justify-between">
                  <label className="text-xs uppercase tracking-[0.18em] text-emerald-200/60">{t.sim_amount}</label>
                  <span className="text-2xl font-bold text-[#e9c767]">${amount.toLocaleString('en-US')}</span>
                </div>
                <input type="range" min={minInv} max={1000000} step={100} value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  className="mt-3 w-full accent-[#e9c767]" data-testid="sim-amount-slider" />
                <div className="flex justify-between text-[10px] text-emerald-200/40 mt-1"><span>${minInv}</span><span>$1,000,000</span></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-2xl border border-[#e9c767]/15 bg-[#07160f]/60 p-5"><Stat label={t.sim_month} value={`$${Math.round(amount * rMin / 100).toLocaleString('en-US')} – $${Math.round(amount * rMax / 100).toLocaleString('en-US')}`} pending={pend} /></div>
                <div className="rounded-2xl border border-[#e9c767]/15 bg-[#07160f]/60 p-5"><Stat label={t.sim_year} value={`$${Math.round(amount * rMin * 12 / 100).toLocaleString('en-US')} – $${Math.round(amount * rMax * 12 / 100).toLocaleString('en-US')}`} pending={pend} /></div>
                <div className="rounded-2xl border border-[#e9c767]/15 bg-[#07160f]/60 p-5"><Stat label={t.sim_mb} value={`${(amount * mbPerUsd).toLocaleString('en-US')} ${mbSym}`} pending={pend} gold /></div>
                <label className="rounded-2xl border border-[#e9c767]/15 bg-[#07160f]/60 p-5 flex items-center justify-between cursor-pointer" data-testid="sim-reinvest-toggle">
                  <span className="text-xs uppercase tracking-[0.12em] text-emerald-200/60">{t.sim_reinvest}</span>
                  <input type="checkbox" checked={reinvest} onChange={(e) => setReinvest(e.target.checked)} className="w-5 h-5 accent-[#e9c767]" />
                </label>
              </div>
              <Link to={startHref} className="inline-flex items-center gap-2 rounded-full bg-[#e9c767] px-7 py-3.5 text-sm font-bold text-[#07160f] hover:brightness-110 transition-all" data-testid="sim-invest-btn">
                {t.cta_start} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="lg:col-span-3 rounded-3xl border border-[#e9c767]/15 bg-[#07160f]/60 p-6">
              <p className="text-xs uppercase tracking-[0.18em] text-emerald-200/60 mb-4">{t.sim_proj}</p>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={simData}>
                  <defs>
                    <linearGradient id="gHigh" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#e9c767" stopOpacity={0.5} /><stop offset="100%" stopColor="#e9c767" stopOpacity={0} /></linearGradient>
                    <linearGradient id="gLow" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3ba776" stopOpacity={0.4} /><stop offset="100%" stopColor="#3ba776" stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(233,199,103,0.08)" />
                  <XAxis dataKey="m" stroke="rgba(255,255,255,0.4)" fontSize={11} />
                  <YAxis stroke="rgba(255,255,255,0.4)" fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ background: '#0a1f15', border: '1px solid rgba(233,199,103,0.3)', borderRadius: 12, color: '#fff' }} formatter={(v) => `$${Number(v).toLocaleString('en-US')}`} />
                  <Area type="monotone" dataKey="high" stroke="#e9c767" strokeWidth={2} fill="url(#gHigh)" />
                  <Area type="monotone" dataKey="low" stroke="#3ba776" strokeWidth={2} fill="url(#gLow)" />
                </AreaChart>
              </ResponsiveContainer>
              <p className="mt-3 text-[11px] text-emerald-200/40 italic">{t.sim_note}</p>
            </div>
          </div>
        </Section>
      </div>

      {/* PILLARS */}
      <Section>
        <h2 className="text-3xl md:text-4xl font-bold mb-10">{t.pillars_title}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {t.pillars.map((p, i) => (
            <div key={i} className="rounded-2xl border border-[#e9c767]/12 bg-[#0a1f15] p-6 hover:border-[#e9c767]/35 transition-colors">
              <CheckCircle2 className="w-5 h-5 text-[#e9c767] mb-3" />
              <p className="font-semibold text-white">{p[0]}</p><p className="text-sm text-emerald-100/60 mt-1">{p[1]}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* FREE SINGLE ACCOUNT */}
      <div className="bg-[#0a1f15] border-y border-[#e9c767]/10">
        <Section>
          <div className="rounded-3xl border border-[#e9c767]/25 bg-[radial-gradient(circle_at_30%_20%,rgba(233,199,103,0.12),transparent_60%)] p-10 md:p-14 text-center">
            <h2 className="text-3xl md:text-4xl font-bold">{t.fsa_title}</h2>
            <p className="mt-4 text-emerald-100/70 max-w-xl mx-auto">{t.fsa_sub}</p>
            <div className="mt-8 flex flex-wrap justify-center gap-10">
              <div><p className="text-xs uppercase tracking-[0.18em] text-emerald-200/60">{t.fsa_setup}</p><p className="text-4xl font-extrabold text-[#e9c767]">US$ 0</p></div>
              <div><p className="text-xs uppercase tracking-[0.18em] text-emerald-200/60">{t.fsa_min}</p><p className="text-4xl font-extrabold text-white">{fmtUSD(minInv)}</p></div>
            </div>
            <Link to="/signup" className="mt-10 inline-flex items-center gap-2 rounded-full bg-[#e9c767] px-8 py-4 text-sm font-bold text-[#07160f] hover:brightness-110 transition-all shadow-[0_0_30px_rgba(233,199,103,0.3)]" data-testid="fsa-open-btn">
              {t.cta_open} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Section>
      </div>

      {/* FINAL CTA */}
      <Section className="text-center">
        <img src={LOGO} alt="Moringa Agro Invest" className="mx-auto h-24 w-auto mb-8 drop-shadow-[0_0_30px_rgba(233,199,103,0.3)]" />
        <h2 className="text-4xl md:text-5xl font-extrabold leading-tight">
          <span className="text-white">{t.final_a}</span> <span className="text-[#e9c767]">{t.final_b}</span> <span className="text-white">{t.final_c}</span>
        </h2>
        <p className="mt-5 text-emerald-100/70 max-w-xl mx-auto">{t.final_sub}</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/signup" className="inline-flex items-center gap-2 rounded-full bg-[#e9c767] px-7 py-3.5 text-sm font-bold text-[#07160f] hover:brightness-110 transition-all" data-testid="final-open-btn">{t.cta_open}</Link>
          <Link to={startHref} className="inline-flex items-center gap-2 rounded-full border border-[#e9c767]/40 px-7 py-3.5 text-sm font-semibold text-[#e9c767] hover:bg-[#e9c767]/10 transition-colors" data-testid="final-start-btn">{t.cta_start}</Link>
        </div>
        <div className="mt-14 flex flex-wrap justify-center gap-x-3 gap-y-1 text-lg md:text-xl font-semibold">
          <span className="text-[#e9c767]">{t.inst_1}</span><span className="text-white">{t.inst_2}</span><span className="text-emerald-100/70">{t.inst_3}</span>
        </div>
        <p className="mt-10 text-xs text-emerald-200/40">Powered by Youse Capital Single Account · Sparta Capital</p>
      </Section>

      {/* FOOTER */}
      <footer className="border-t border-[#e9c767]/15 bg-[#050f0a]" data-testid="moringa-footer">
        <div className="mx-auto max-w-7xl px-6 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src={LOGO} alt="Moringa Agro Invest" className="h-11 w-auto" />
              <div>
                <p className="text-sm font-semibold text-[#e9c767]">MORINGA AGRO INVEST</p>
                <p className="text-xs text-emerald-200/50">Capital Connected to Real Production</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
              <a href="https://moringabank.com" target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-emerald-100/70 hover:text-[#e9c767] transition-colors" data-testid="footer-moringabank-link">
                <Globe2 className="w-4 h-4" /> moringabank.com
              </a>
              <span className="hidden md:block h-4 w-px bg-[#e9c767]/20" />
              <span className="flex items-center gap-1.5 text-emerald-100/70">
                <Landmark className="w-4 h-4" /> Youse Capital LLC
              </span>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-[#e9c767]/10 text-center">
            <p className="text-xs text-emerald-200/40">
              © {new Date().getFullYear()} Moringa Agro Invest — a product of <span className="text-emerald-100/60">moringabank.com</span> &amp; <span className="text-emerald-100/60">Youse Capital LLC</span>. All rights reserved.
            </p>
            <p className="mt-2 text-[11px] text-emerald-200/30">
              Projected / target figures shown across this page are not a guarantee of return. Agricultural investments involve risk.
            </p>
          </div>
        </div>
      </footer>

      {/* QUICK INTERACTIVE INVESTMENT FLOW */}
      <MoringaSimulator cfg={c} user={user} />
    </div>
  );
}
